// V3 Profile Factors editor. Each dimension expands into an option list, and
// each option carries a `targetedFactors` array — entries that target specific
// disciplines × levels with a multiplier.

import { useState } from 'react';
import clsx from 'clsx';
import { Plus, X, ChevronDown, ChevronRight } from 'lucide-react';
import { LEVEL_GROUPS, groupsForLevels, levelsForGroups, type LevelGroupId } from '@rc/engine';
import type {
  Dimension,
  DimensionOption,
  Model,
  TargetedFactor,
} from '@rc/engine/types';

interface Props {
  model: Model;
  onChange: (m: Model) => void;
}

export default function ProfileFactorsEditor({ model, onChange }: Props) {
  const dims = model.dimensions || [];

  const setDim = (i: number, next: Dimension) => {
    const all = [...dims];
    all[i] = next;
    onChange({ ...model, dimensions: all });
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500">
        Profile factors are dimensions populated by the client intake. Each option carries optional
        targeted multipliers (per-discipline / per-level) that the engine applies in Stage 3.
      </p>
      {dims.map((dim, i) => (
        <DimensionBlock key={dim.id} dim={dim} model={model} onChange={(next) => setDim(i, next)} />
      ))}
    </div>
  );
}

function DimensionBlock({
  dim,
  model,
  onChange,
}: {
  dim: Dimension;
  model: Model;
  onChange: (d: Dimension) => void;
}) {
  const [open, setOpen] = useState(false);

  const setOpt = (j: number, opt: DimensionOption) => {
    const next = [...(dim.options || [])];
    next[j] = opt;
    onChange({ ...dim, options: next });
  };

  return (
    <details
      open={open}
      onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}
      className="border border-slate-200 rounded-lg overflow-hidden"
    >
      <summary className="px-4 py-3 cursor-pointer hover:bg-slate-50 text-sm font-medium flex items-center justify-between list-none">
        <span className="inline-flex items-center gap-2">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          {dim.label}
          <span className="text-xs text-slate-400 font-normal">{dim.appliesTo}</span>
          {dim.kind === 'multiselect' && (
            <span className="text-[10px] uppercase tracking-wide font-semibold text-brand-purple-600 bg-brand-purple-50 border border-brand-purple-100 rounded px-1.5 py-0.5">
              multiselect
            </span>
          )}
        </span>
        <span className="text-xs text-slate-400">{dim.options.length} options</span>
      </summary>
      <div className="bg-slate-50/40 border-t border-slate-100 p-3 space-y-3">
        {dim.options.map((opt, j) => (
          <OptionRow
            key={opt.value}
            opt={opt}
            model={model}
            onChange={(next) => setOpt(j, next)}
          />
        ))}
      </div>
    </details>
  );
}

function OptionRow({
  opt,
  model,
  onChange,
}: {
  opt: DimensionOption;
  model: Model;
  onChange: (o: DimensionOption) => void;
}) {
  const [tfOpen, setTfOpen] = useState(false);
  const tf = opt.targetedFactors || [];

  const updateTf = (i: number, patch: Partial<TargetedFactor>) => {
    const next = [...tf];
    next[i] = { ...next[i], ...patch };
    onChange({ ...opt, targetedFactors: next });
  };
  const addTf = () =>
    onChange({
      ...opt,
      targetedFactors: [...tf, { factor: 1, disciplines: [], levels: [] }],
    });
  const removeTf = (i: number) =>
    onChange({ ...opt, targetedFactors: tf.filter((_, j) => j !== i) });

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="flex items-center gap-2 text-sm">
        <input
          className="input py-1 px-2 flex-1"
          value={opt.label}
          onChange={(e) => onChange({ ...opt, label: e.target.value })}
        />
        <code className="text-xs font-mono text-slate-400 px-1">{opt.value}</code>
        <span className="text-xs text-slate-500">×</span>
        <input
          type="number"
          step={0.01}
          className="input py-1 px-2 w-20 text-right"
          value={opt.factor ?? 1}
          onChange={(e) => onChange({ ...opt, factor: Number(e.target.value) })}
          title="Blanket multiplier (used when no targetedFactor matches a cell)"
        />
        <button
          type="button"
          onClick={() => setTfOpen((o) => !o)}
          className={clsx(
            'pill border text-xs transition-colors',
            tf.length > 0
              ? 'bg-brand-purple-50 text-brand-purple-700 border-brand-purple-200'
              : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300',
          )}
        >
          {tf.length === 0 ? 'No targeted' : `${tf.length} targeted`}
        </button>
      </div>
      {tfOpen && (
        <div className="mt-3 space-y-2 border-t border-slate-100 pt-3">
          {tf.length === 0 && (
            <p className="text-xs text-slate-400">No targeted multipliers yet.</p>
          )}
          {tf.map((entry, i) => (
            <TargetedFactorRow
              key={i}
              entry={entry}
              model={model}
              onChange={(patch) => updateTf(i, patch)}
              onRemove={() => removeTf(i)}
            />
          ))}
          <button
            type="button"
            onClick={addTf}
            className="text-xs text-brand-purple-700 hover:text-brand-purple-900 inline-flex items-center gap-1"
          >
            <Plus size={12} /> Add targeted multiplier
          </button>
        </div>
      )}
    </div>
  );
}

function TargetedFactorRow({
  entry,
  model,
  onChange,
  onRemove,
}: {
  entry: TargetedFactor;
  model: Model;
  onChange: (patch: Partial<TargetedFactor>) => void;
  onRemove: () => void;
}) {
  return (
    <div className="rounded-lg border border-slate-200 p-2 grid grid-cols-1 md:grid-cols-[80px_1fr_1fr_auto] gap-2 items-start text-sm">
      <div>
        <label className="text-[10px] uppercase tracking-wider text-slate-500">Factor</label>
        <input
          type="number"
          step={0.01}
          className="input py-1 px-2 w-full text-right"
          value={entry.factor}
          onChange={(e) => onChange({ factor: Number(e.target.value) })}
        />
      </div>
      <MultiSelect
        label="Disciplines"
        options={(model.disciplines || []).map((d) => ({ id: d.id, label: d.label }))}
        value={entry.disciplines || []}
        onChange={(disciplines) => onChange({ disciplines })}
        emptyLabel="All disciplines"
      />
      <MultiSelect
        label="Levels"
        options={LEVEL_GROUPS.map((g) => ({ id: g.id, label: g.label }))}
        value={groupsForLevels(entry.levels)}
        onChange={(groups) => onChange({ levels: levelsForGroups(groups as LevelGroupId[]) })}
        emptyLabel="All levels"
      />
      <button
        type="button"
        onClick={onRemove}
        className="text-slate-400 hover:text-rose-500 mt-5"
        aria-label="Remove"
      >
        <X size={14} />
      </button>
    </div>
  );
}

function MultiSelect({
  label,
  options,
  value,
  onChange,
  emptyLabel,
}: {
  label: string;
  options: { id: string; label: string }[];
  value: string[];
  onChange: (next: string[]) => void;
  emptyLabel: string;
}) {
  const selected = new Set(value);
  const toggle = (id: string) => {
    const next = selected.has(id) ? value.filter((v) => v !== id) : [...value, id];
    onChange(next);
  };
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-slate-500">{label}</label>
      <div className="flex flex-wrap gap-1 mt-1">
        {value.length === 0 && (
          <span className="text-xs text-slate-400 italic">{emptyLabel}</span>
        )}
        {options.map((o) => {
          const on = selected.has(o.id);
          return (
            <button
              key={o.id}
              type="button"
              onClick={() => toggle(o.id)}
              className={clsx(
                'text-xs px-2 py-0.5 rounded-md border transition-colors',
                on
                  ? 'bg-brand-purple-100 text-brand-purple-700 border-brand-purple-300'
                  : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300',
              )}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
